import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef
} from 'react';

import Notification from './Notification';
import ConfirmationDialog from './ConfirmationDialog';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }

  return context;
};

const makeClientId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {}

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const storageKeyForUser = (userId) =>
  `mercadobulk_notifications:${userId}`;

const normalizeNotification = (notification = {}) => {
  const clientId = String(
    notification?.clientId ||
    notification?.client_id ||
    notification?.id ||
    makeClientId()
  );

  return {
    id: clientId,
    clientId,
    type: notification?.type || 'info',
    title: notification?.title || '',
    message: notification?.message || '',
    duration: Number(notification?.duration) || 0,
    persistent: Boolean(notification?.persistent),
    createdAt: notification?.createdAt
      ? new Date(notification.createdAt)
      : notification?.created_at
        ? new Date(notification.created_at)
        : new Date(),
    actions: [],
    cloudSynced: notification?.cloudSynced === true
  };
};

const serializeLocal = (notifications = []) =>
  notifications.map((notification) => ({
    id: String(notification?.id || notification?.clientId),
    clientId: String(notification?.clientId || notification?.id),
    type: notification?.type || 'info',
    title: notification?.title || '',
    message: notification?.message || '',
    duration: Number(notification?.duration) || 0,
    persistent: Boolean(notification?.persistent),
    createdAt:
      notification?.createdAt instanceof Date
        ? notification.createdAt.toISOString()
        : notification?.createdAt || new Date().toISOString(),
    cloudSynced: notification?.cloudSynced === true
  }));

const saveLocal = (userId, notifications) => {
  if (!userId) return;

  try {
    window.localStorage.setItem(
      storageKeyForUser(userId),
      JSON.stringify(serializeLocal(notifications))
    );
  } catch (error) {
    console.error(
      'No se pudieron guardar las notificaciones localmente:',
      error?.message || error
    );
  }
};

const readLocal = (userId) => {
  if (!userId) return [];

  try {
    const raw = window.localStorage.getItem(
      storageKeyForUser(userId)
    );

    if (!raw) return [];

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return [];

    return parsed.map(normalizeNotification);
  } catch (error) {
    console.error(
      'No se pudieron leer las notificaciones locales:',
      error?.message || error
    );

    return [];
  }
};

const toDatabaseRow = (userId, notification) => ({
  user_id: userId,
  client_id: String(
    notification?.clientId || notification?.id
  ),
  type: notification?.type || 'info',
  title: notification?.title || '',
  message: notification?.message || '',
  duration: Number(notification?.duration) || 0,
  persistent: Boolean(notification?.persistent),
  is_read: false,
  created_at:
    notification?.createdAt instanceof Date
      ? notification.createdAt.toISOString()
      : notification?.createdAt || new Date().toISOString()
});

const fromDatabaseRow = (row) =>
  normalizeNotification({
    id: row?.client_id,
    clientId: row?.client_id,
    type: row?.type,
    title: row?.title,
    message: row?.message,
    duration: row?.duration,
    persistent: row?.persistent,
    created_at: row?.created_at,
    cloudSynced: true
  });

const sortNotifications = (items = []) =>
  [...items].sort(
    (a, b) =>
      new Date(a?.createdAt || 0).getTime() -
      new Date(b?.createdAt || 0).getTime()
  );

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [toastNotifications, setToastNotifications] = useState([]);
  const [confirmations, setConfirmations] = useState([]);

  const userIdRef = useRef(null);
  const loadSequenceRef = useRef(0);

  const currentUserId = user?.id || null;
  userIdRef.current = currentUserId;

  const loadNotificationsForUser = useCallback(async (userId) => {
    const sequence = ++loadSequenceRef.current;

    userIdRef.current = userId || null;

    if (!userId) {
      setNotifications([]);
      setToastNotifications([]);
      return;
    }

    let localItems = readLocal(userId);

    /*
     * Migración / recuperación:
     * cualquier notificación local que todavía no figure como sincronizada
     * se intenta subir a Supabase.
     */
    const unsynced = localItems.filter(
      (notification) => notification?.cloudSynced !== true
    );

    if (unsynced.length > 0) {
      try {
        const rows = unsynced.map((notification) =>
          toDatabaseRow(userId, notification)
        );

        const { error } = await supabase
          .from('notifications')
          .upsert(rows, {
            onConflict: 'user_id,client_id'
          });

        if (error) {
          console.error(
            'No se pudieron migrar notificaciones locales:',
            error.message
          );
        } else {
          const migratedIds = new Set(
            unsynced.map((notification) =>
              String(notification?.clientId || notification?.id)
            )
          );

          localItems = localItems.map((notification) =>
            migratedIds.has(
              String(notification?.clientId || notification?.id)
            )
              ? {
                  ...notification,
                  cloudSynced: true
                }
              : notification
          );

          saveLocal(userId, localItems);
        }
      } catch (error) {
        console.error(
          'Error migrando notificaciones:',
          error?.message || error
        );
      }
    }

    try {
      const { data, error } = await supabase
        .from('notifications')
        .select(
          'id,user_id,client_id,type,title,message,duration,persistent,is_read,created_at,updated_at'
        )
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      if (sequence !== loadSequenceRef.current) {
        return;
      }

      const remoteItems = Array.isArray(data)
        ? data.map(fromDatabaseRow)
        : [];

      /*
       * Supabase es la fuente principal.
       * Solo conservamos adicionalmente datos locales que todavía
       * estén pendientes de sincronización.
       */
      const pendingLocal = localItems.filter(
        (notification) => notification?.cloudSynced !== true
      );

      const mergedMap = new Map();

      remoteItems.forEach((notification) => {
        mergedMap.set(
          String(notification?.clientId || notification?.id),
          notification
        );
      });

      pendingLocal.forEach((notification) => {
        const key = String(
          notification?.clientId || notification?.id
        );

        if (!mergedMap.has(key)) {
          mergedMap.set(key, notification);
        }
      });

      const merged = sortNotifications(
        Array.from(mergedMap.values())
      );

      saveLocal(userId, merged);
      setNotifications(merged);
    } catch (error) {
      console.error(
        'No se pudieron descargar las notificaciones de Supabase:',
        error?.message || error
      );

      /*
       * Si Supabase falla temporalmente, la app sigue mostrando
       * el respaldo local.
       */
      if (sequence === loadSequenceRef.current) {
        setNotifications(sortNotifications(localItems));
      }
    }
  }, []);

  const syncNotificationToCloud = useCallback(
    async (notification) => {
      const userId = userIdRef.current;

      if (!userId) return;

      try {
        const { error } = await supabase
          .from('notifications')
          .upsert(
            toDatabaseRow(userId, notification),
            {
              onConflict: 'user_id,client_id'
            }
          );

        if (error) {
          throw error;
        }

        setNotifications((prev) => {
          const next = prev.map((item) =>
            String(item?.clientId || item?.id) ===
            String(notification?.clientId || notification?.id)
              ? {
                  ...item,
                  cloudSynced: true
                }
              : item
          );

          saveLocal(userId, next);

          return next;
        });
      } catch (error) {
        console.error(
          'No se pudo sincronizar la notificación:',
          error?.message || error
        );
      }
    },
    []
  );

  useEffect(() => {
    loadNotificationsForUser(currentUserId);
  }, [currentUserId, loadNotificationsForUser]);

  const addNotification = useCallback(
    ({
      type = 'info',
      title,
      message,
      duration = 5000,
      actions = [],
      persistent = false
    }) => {
      const clientId = makeClientId();

      const notification = {
        id: clientId,
        clientId,
        type,
        title,
        message,
        duration,
        actions,
        persistent,
        createdAt: new Date(),
        cloudSynced: false
      };

      setNotifications((prev) => {
        const next = [...prev, notification];

        saveLocal(userIdRef.current, next);

        return next;
      });

      setToastNotifications((prev) => [
        ...prev,
        notification
      ]);

      // Guardar también en Supabase sin bloquear la interfaz.
      void syncNotificationToCloud(notification);

      // Ocultar solo el toast flotante.
      if (!persistent && duration > 0) {
        setTimeout(() => {
          setToastNotifications((prev) =>
            prev?.filter(
              (notif) => notif?.id !== clientId
            )
          );
        }, duration);
      }

      return clientId;
    },
    [syncNotificationToCloud]
  );

  const removeNotification = useCallback((id) => {
    const userId = userIdRef.current;
    const clientId = String(id);

    setNotifications((prev) => {
      const next = prev.filter(
        (notification) =>
          String(
            notification?.clientId || notification?.id
          ) !== clientId
      );

      saveLocal(userId, next);

      return next;
    });

    setToastNotifications((prev) =>
      prev.filter(
        (notification) =>
          String(
            notification?.clientId || notification?.id
          ) !== clientId
      )
    );

    if (userId) {
      void supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId)
        .eq('client_id', clientId)
        .then(({ error }) => {
          if (error) {
            console.error(
              'No se pudo borrar la notificación de Supabase:',
              error.message
            );
          }
        });
    }
  }, []);

  const clearAllNotifications = useCallback(() => {
    const userId = userIdRef.current;

    setNotifications([]);
    setToastNotifications([]);

    saveLocal(userId, []);

    if (userId) {
      void supabase
        .from('notifications')
        .delete()
        .eq('user_id', userId)
        .then(({ error }) => {
          if (error) {
            console.error(
              'No se pudieron borrar las notificaciones de Supabase:',
              error.message
            );
          }
        });
    }
  }, []);

  const showSuccess = useCallback(
    (message, options = {}) =>
      addNotification({
        type: 'success',
        title: options?.title || 'Éxito',
        message,
        duration: options?.duration || 4000,
        ...options
      }),
    [addNotification]
  );

  const showError = useCallback(
    (message, options = {}) =>
      addNotification({
        type: 'error',
        title: options?.title || 'Error',
        message,
        duration: options?.duration || 6000,
        persistent: options?.persistent || false,
        ...options
      }),
    [addNotification]
  );

  const showWarning = useCallback(
    (message, options = {}) =>
      addNotification({
        type: 'warning',
        title: options?.title || 'Advertencia',
        message,
        duration: options?.duration || 5000,
        ...options
      }),
    [addNotification]
  );

  const showInfo = useCallback(
    (message, options = {}) =>
      addNotification({
        type: 'info',
        title: options?.title || 'Información',
        message,
        duration: options?.duration || 4000,
        ...options
      }),
    [addNotification]
  );

  const showConfirmation = useCallback(
    ({
      title = 'Confirmar acción',
      message,
      confirmText = 'Confirmar',
      cancelText = 'Cancelar',
      type = 'warning',
      onConfirm,
      onCancel
    }) => {
      const id = makeClientId();

      const confirmation = {
        id,
        title,
        message,
        confirmText,
        cancelText,
        type,

        onConfirm: () => {
          setConfirmations((prev) =>
            prev.filter(
              (confirmation) =>
                confirmation?.id !== id
            )
          );

          onConfirm?.();
        },

        onCancel: () => {
          setConfirmations((prev) =>
            prev.filter(
              (confirmation) =>
                confirmation?.id !== id
            )
          );

          onCancel?.();
        }
      };

      setConfirmations((prev) => [
        ...prev,
        confirmation
      ]);

      return id;
    },
    []
  );


  // MCO_ALERTA_CARGA_REAL_V1
  const updateNotification = useCallback((id, changes = {}) => {
    const objetivo = String(id);

    const aplicar = notificacion =>
      String(notificacion?.clientId || notificacion?.id) === objetivo
        ? { ...notificacion, ...changes }
        : notificacion;

    setToastNotifications(prev => prev.map(aplicar));
    setNotifications(prev => prev.map(aplicar));
  }, []);

  const value = {
    updateNotification,
    notifications,
    confirmations,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showConfirmation
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}

      <div className="fixed top-20 right-4 z-50 space-y-2 max-w-md">
        {toastNotifications?.map((notification) => (
          <Notification
            key={notification?.id}
            notification={notification}
            onDismiss={() =>
              setToastNotifications((prev) =>
                prev.filter(
                  (notif) =>
                    notif?.id !== notification?.id
                )
              )
            }
          />
        ))}
      </div>

      {confirmations?.map((confirmation) => (
        <ConfirmationDialog
          key={confirmation?.id}
          {...confirmation}
        />
      ))}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
